import React, { useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { TaskTemplateInputGroupType } from "api/model/enums";
import {
  TaskTemplateInputGroupDefinition,
  TaskTemplateInputFromSubgroup,
  TaskTemplateDefinitionDTO,
} from "api/model/airflow-task/task-template/TaskTemplateInfo";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import InputGroupedDefinitionsSubFormsList from "./InputGroupedDefinitionsSubFormsList";
import { createObject, handleMiniDialogCancel } from "./TemplateEditorUtils";
import "./TemplateEditor.css";

interface TemplateEditorProps {
  initialData?: string;
  handleDefChange?: (data: TaskTemplateDefinitionDTO) => void;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({ initialData, handleDefChange }) => {
  const initialDataJson = initialData ? JSON.parse(initialData) : {};
  const [inputGroupDefinitions, setInputGroupDefinitions] = useState<
    TaskTemplateInputGroupDefinition[]
  >(initialDataJson?.inputGroupDefinitions || []);
  const [selectedGroup, setSelectedGroup] = useState<TaskTemplateInputGroupDefinition>();
  const [newGroupId, setNewGroupId] = useState<string>("");
  const [newGroupHeader, setNewGroupHeader] = useState<string>("");
  const [miniDialog, setMiniDialog] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tempId, setTempId] = useState<string>("");

  const taskTemplateInputGroupOptions = Object.keys(TaskTemplateInputGroupType).map(key => ({
    label: TaskTemplateInputGroupType[key as keyof typeof TaskTemplateInputGroupType],
    value: TaskTemplateInputGroupType[key as keyof typeof TaskTemplateInputGroupType],
  }));

  const deleteTemplate = (index: number) => {
    setInputGroupDefinitions(prevTemplates => {
      const updatedTemplates = [...prevTemplates];
      updatedTemplates.splice(index, 1);
      return updatedTemplates;
    });
  };

  const updateTemplate = (
    id: string,
    key: keyof TaskTemplateInputGroupDefinition,
    value: string
  ) => {
    const valueToUpdate = key === "subForms" ? JSON.parse(value) : value;
    setInputGroupDefinitions(prevTemplates => {
      const updatedTemplates = prevTemplates.map(template => {
        if (template.id === id) {
          const newTemplate = { ...template, [key]: valueToUpdate };
          setSelectedGroup(newTemplate);
          return newTemplate;
        }
        return template;
      });
      return updatedTemplates;
    });
  };

  const confirmOnDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
    confirmPopup({
      target: e.currentTarget,
      message: "Are you sure you want to delete this Input Grouped Definition?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => deleteTemplate(index),
    });
  };

  const confirmOnIdChange = (newId: string) => {
    confirmPopup({
      message: "Are you sure you want to change the ID?",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        if (selectedGroup && !inputGroupDefinitions.find(group => group.id === newId)) {
          updateTemplate(selectedGroup.id, "id", newId);
          setEditingId(null);
          easasAlert({
            severity: "success",
            summary: `ID changed to ${newId}`,
          })
        } else {
          easasAlert({
            severity: "error",
            summary: `ID ${newId} already exists. Please choose a different ID.`,
          })
        }
      },
    });
  };

  const onSubFormsChange = (newSubForms: TaskTemplateInputFromSubgroup[]) => {
    setSelectedGroup(prevGroupData => {
      if (prevGroupData) {
        const updatedGroup: TaskTemplateInputGroupDefinition = {
          id: prevGroupData.id,
          type: prevGroupData.type,
          header: prevGroupData.header,
          subForms: newSubForms,
        };
        updateTemplate(prevGroupData.id, "subForms", JSON.stringify(newSubForms));
        return updatedGroup;
      }
      return prevGroupData;
    });
  };

  const handleSave = () => {
    if (handleDefChange) {
      handleDefChange({ inputGroupDefinitions });
    }
  };

  const handleMiniDialogSubmit = () => {
    const currentIds = inputGroupDefinitions.map(group => group.id);
    if (currentIds.includes(newGroupId)) {
      easasAlert({
        severity: "error",
        summary: `Input ID ${newGroupId} already exists. Please choose a different ID.`,
      });

      return;
    }
    setInputGroupDefinitions(prevGroups => {
      const newGroup: TaskTemplateInputGroupDefinition = {
        id: newGroupId,
        type: TaskTemplateInputGroupType.GROUPED,
        header: newGroupHeader,
        subForms: [],
      };
      return [...prevGroups, newGroup];
    });
    setMiniDialog(false);
    setNewGroupId("");
    setNewGroupHeader("");
  };

  const onRowReorder = (e: { value: TaskTemplateInputGroupDefinition[] }) => {
    setInputGroupDefinitions(e.value);
  };

  const renderIdInput = (
    rowData: TaskTemplateInputGroupDefinition,
    isTable: boolean,
    confirmOnChange?: (value: string) => void
  ) => (
    <div>
      {isTable ? (
        <div className="flex flex-row">
          <div>{rowData.id}</div>
        </div>
      ) : (
        <div>
          <InputText
            placeholder="Id"
            value={editingId === rowData.id ? tempId : rowData.id}
            onChange={e => {
              setEditingId(rowData.id);
              setTempId(e.target.value);
            }}
            onClick={() => setSelectedGroup(rowData)}
            className="p-inputtext-sm"
          />
          {selectedGroup && editingId === selectedGroup.id && confirmOnChange && (
            <Button
              label="Update"
              className="p-button-sm p-button-primary w-5rem"
              onClick={() => confirmOnChange(tempId)}
            />
          )}
        </div>
      )}
    </div>
  );

  const renderHeaderInput = (rowData: TaskTemplateInputGroupDefinition, isTable: boolean) => (
    <div>
      {isTable ? (
        <div>{rowData.header}</div>
      ) : (
        <InputText
          placeholder="Header"
          value={rowData.header}
          onChange={e => updateTemplate(rowData.id, "header", e.target.value)}
          className="p-inputtext-sm"
        />
      )}
    </div>
  );

  const renderDeleteButton = (rowData: TaskTemplateInputGroupDefinition) => (
    <Button
      icon="pi pi-trash"
      className="p-button-rounded p-button-text p-button-danger"
      onClick={e => confirmOnDelete(e, Number(rowData.id))}
    />
  );

  return (
    <div>
      <Button
        className="p-button-success w-max mb-3"
        label="Add Group"
        onClick={() => createObject(setMiniDialog)}
      />
      <DataTable
        header="Groups"
        value={inputGroupDefinitions}
        reorderableRows
        onRowReorder={onRowReorder}
        editMode="cell"
        dataKey="id"
        selection={selectedGroup}
        onSelectionChange={e => {
          if (e.value.length > 1) {
            return null;
          }
          setSelectedGroup(e.value);
          return null;
        }}
        responsiveLayout="scroll"
        scrollHeight="300px"
        size="small"
        className="p-0"
      >
        <Column selectionMode="single" style={{ width: "2%", padding: "0.3rem" }} />
        <Column
          field="id"
          style={{ minWidth: "100px", padding: 0 }}
          className="overflow-hidden text-overflow-ellipsis"
          header="Id"
          body={rowData => renderIdInput(rowData, true)}
        />
        <Column field="header" header="Header" style={{ width: "60%", padding: 0 }} body={rowData => renderHeaderInput(rowData, true)} />
        <Column style={{ width: "5%", padding: 0  }} body={rowData => renderDeleteButton(rowData)} />
        <Column rowReorder style={{ justifyContent: "end", width: "5%", padding: 0  }} />
      </DataTable>
      {selectedGroup && (
        <div className="subform-container mt-4">
          <h4>Group {selectedGroup.header}</h4>
          <div className="flexRow">
            <div className="p-field p-col-4">
              <label>Id:</label>
              {renderIdInput(selectedGroup, false, confirmOnIdChange)}
            </div>
            <div className="p-field p-col-4">
              <label htmlFor="type">Type:</label>
              <Dropdown
                value={selectedGroup.type}
                options={taskTemplateInputGroupOptions}
                filter
                showClear
                onChange={e => updateTemplate(selectedGroup.id, "type", e.value)}
                optionLabel="label"
                placeholder="Select a type"
                className="p-inputtext-sm"
              />
            </div>
            <div className="p-field p-col-4">
              <label>Header:</label>
              <InputText
                placeholder="Header"
                value={selectedGroup.header}
                onChange={e => updateTemplate(selectedGroup.id, "header", e.target.value)}
                className="p-inputtext-sm"
              />
            </div>
          </div>
          <InputGroupedDefinitionsSubFormsList
            subForms={selectedGroup.subForms}
            onSubFormsChange={newSubForms => onSubFormsChange(newSubForms)}
          />
        </div>
      )}
      <div className="field col-12">
        <Button
          label="Apply template"
          className="p-button-success"
          icon="pi pi-check"
          onClick={handleSave}
        />
      </div>
      <Dialog
        header="Create New Group"
        blockScroll
        visible={miniDialog}
        onHide={() => setMiniDialog(false)}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() =>
                handleMiniDialogCancel(setMiniDialog, setNewGroupId, setNewGroupHeader)
              }
              className="p-button-sm p-button-danger"
            />
            <Button
              label="Create"
              icon="pi pi-check"
              onClick={handleMiniDialogSubmit}
              className="p-button-sm p-button-success"
            />
          </div>
        }
      >
        <div className="p-field">
          <label htmlFor="newGroupId">ID</label>
          <InputText
            id="newGroupId"
            value={newGroupId}
            onChange={e => setNewGroupId(e.target.value)}
            className="p-inputtext-sm"
          />
        </div>
        <div className="p-field">
          <label htmlFor="newGroupHeader">Header</label>
          <InputText
            id="newGroupHeader"
            value={newGroupHeader}
            onChange={e => setNewGroupHeader(e.target.value)}
            className="p-inputtext-sm"
          />
        </div>
      </Dialog>
    </div>
  );
};

export default TemplateEditor;
